"use client";

import {
  APIProvider,
  InfoWindow,
  Map,
  Marker,
  useMap,
} from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";
import type { AcademyLocationCard } from "@/features/academy-locations/queries";
import type { PlaceSuggestion } from "@/features/places/search";
import { registerHref, STATUS_LABELS } from "./format";

type Props = {
  apiKey: string;
  locations: AcademyLocationCard[];
  origin: PlaceSuggestion | null;
};

// Great Britain, before anything is searched for.
const UK_CENTRE = { lat: 54.3, lng: -2.5 };

/** The locations as pins on a Google map; a pin opens its name and the way to register interest. */
export default function LocationsMap({ apiKey, locations, origin }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = locations.find((location) => location.id === openId);

  return (
    <APIProvider apiKey={apiKey}>
      <div className="h-[420px] w-full overflow-hidden rounded-[18px] mobile:h-[320px]">
        <Map
          defaultCenter={UK_CENTRE}
          defaultZoom={6}
          gestureHandling="cooperative"
          disableDefaultUI
          zoomControl
        >
          {locations.map(
            (location) =>
              location.latitude !== null &&
              location.longitude !== null && (
                <Marker
                  key={location.id}
                  position={{ lat: location.latitude, lng: location.longitude }}
                  title={location.name}
                  onClick={() => setOpenId(location.id)}
                />
              ),
          )}

          {opened && opened.latitude !== null && opened.longitude !== null && (
            <InfoWindow
              position={{ lat: opened.latitude, lng: opened.longitude }}
              onCloseClick={() => setOpenId(null)}
            >
              <div className="font-das">
                <p className="text-[15px] font-semibold text-das-black">
                  {opened.name}
                </p>
                <p className="text-[13px] text-das-gray">{opened.siteName}</p>
                <p className="text-[12px] text-das-gray">
                  {STATUS_LABELS[opened.status]}
                </p>
                <a
                  href={registerHref(opened.slug)}
                  className="mt-2 inline-block text-[13px] font-medium text-blue-bright hover:underline"
                >
                  Register your interest
                </a>
              </div>
            </InfoWindow>
          )}

          <FollowOrigin origin={origin} />
        </Map>
      </div>
    </APIProvider>
  );
}

/** Centres the map on the place searched for. */
function FollowOrigin({ origin }: { origin: PlaceSuggestion | null }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !origin) return;
    map.panTo({ lat: origin.latitude, lng: origin.longitude });
    map.setZoom(9);
  }, [map, origin]);

  return null;
}
